import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Panel } from "@/components/studio/common";
import { api, dt, d, formatErr } from "@/lib/api";

export const TimelineCard = ({ p }) => (
  <Panel title="Timeline" testid="pratica-timeline-card">
    <ol className="relative border-l border-slate-200 ml-1.5 space-y-4 max-h-[480px] overflow-y-auto pr-1">
      {[...p.events].reverse().map((e) => (
        <li key={e.id} className="ml-4">
          <span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full bg-gold" />
          <p className="text-xs text-slate-400">{dt(e.at)} · {e.actor}</p>
          <p className="text-sm text-slate-800">{e.testo}</p>
        </li>
      ))}
    </ol>
  </Panel>
);

export const NotesCard = ({ p, load }) => {
  const [text, setText] = useState("");
  const add = async () => {
    if (!text.trim()) return;
    try { await api.post(`/pratiche/${p.id}/notes`, { text }); setText(""); load(); } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <Panel title="Note interne" testid="pratica-notes-card">
      <Textarea data-testid="note-input" rows={2} placeholder="Aggiungi una nota…" value={text} onChange={(e) => setText(e.target.value)} />
      <Button size="sm" data-testid="note-add-btn" className="mt-2 bg-navy hover:bg-navy-800" onClick={add}>Aggiungi nota</Button>
      <ul className="mt-4 space-y-3">
        {[...p.notes].reverse().map((n) => (
          <li key={n.id} className="text-sm bg-slate-50 rounded-md p-3 border border-slate-100"><p className="text-xs text-slate-400">{n.author} · {dt(n.at)}</p><p className="mt-1 whitespace-pre-wrap">{n.text}</p></li>
        ))}
      </ul>
    </Panel>
  );
};

export const RemindersCard = ({ p, load }) => {
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const add = async () => {
    if (!title.trim() || !due) return toast.error("Titolo e data obbligatori");
    try { await api.post(`/pratiche/${p.id}/reminders`, { title, due_date: due }); setTitle(""); setDue(""); load(); } catch (e) { toast.error(formatErr(e)); }
  };
  const toggle = async (r) => { await api.patch(`/pratiche/${p.id}/reminders/${r.id}`, { done: !r.done }); load(); };
  const today = new Date().toISOString().slice(0, 10);
  return (
    <Panel title="Scadenze" testid="pratica-reminders-card">
      <div className="flex gap-2">
        <Input data-testid="reminder-title-input" placeholder="Es. sollecito Consap" value={title} onChange={(e) => setTitle(e.target.value)} className="h-9" />
        <Input data-testid="reminder-date-input" type="date" value={due} onChange={(e) => setDue(e.target.value)} className="h-9 w-40" />
      </div>
      <Button size="sm" data-testid="reminder-add-btn" variant="outline" className="mt-2" onClick={add}>Aggiungi scadenza</Button>
      <ul className="mt-3 space-y-2">
        {p.reminders.map((r) => (
          <li key={r.id} className="flex items-center gap-2 text-sm" data-testid={`reminder-${r.id}`}>
            <Checkbox checked={r.done} onCheckedChange={() => toggle(r)} data-testid={`reminder-toggle-${r.id}`} />
            <span className={`font-mono text-xs ${!r.done && r.due_date < today ? "text-red-700 font-semibold" : "text-slate-500"}`}>{d(r.due_date)}</span>
            <span className={r.done ? "line-through text-slate-400" : ""}>{r.title}</span>
          </li>
        ))}
      </ul>
    </Panel>
  );
};

export const FlagsCard = ({ p, setP, user }) => {
  const [open, setOpen] = useState(null);
  const [note, setNote] = useState("");
  const [importo, setImporto] = useState("");
  if (user.role === "segretaria") return null;
  const submit = async () => {
    try {
      const { data } = await api.post(`/pratiche/${p.id}/flag`, { tipo: open, note, importo: importo === "" ? null : Number(importo) });
      setP(data); setOpen(null); setNote(""); setImporto("");
      toast.success("Nota di compenso generata e archiviata nei documenti");
    } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <Panel title="Casi patologici" testid="pratica-flags-card">
      <p className="text-xs text-slate-500 mb-3">Segnala revoca o by-pass: viene generata automaticamente la nota di compenso (parcella ministeriale o penale).</p>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" data-testid="flag-revoca-btn" disabled={p.flags?.revoca} className="text-red-700 border-red-200" onClick={() => setOpen("revoca")}>Revoca mandato</Button>
        <Button size="sm" variant="outline" data-testid="flag-bypass-btn" disabled={p.flags?.bypass} className="text-red-700 border-red-200" onClick={() => setOpen("bypass")}>By-pass cliente</Button>
      </div>
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent data-testid="flag-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{open === "revoca" ? "Revoca del mandato" : "By-pass del cliente"}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">{open === "revoca" ? "Importo predefinito: tariffa ministeriale da impostazioni. Puoi indicare un importo specifico." : "Importo predefinito: penale contrattuale da impostazioni (300 €)."}</p>
          <Input data-testid="flag-importo-input" type="number" step="0.01" placeholder="Importo (vuoto = predefinito)" value={importo} onChange={(e) => setImporto(e.target.value)} />
          <Textarea data-testid="flag-note-input" placeholder="Motivazione / note" value={note} onChange={(e) => setNote(e.target.value)} />
          <DialogFooter><Button data-testid="flag-confirm-btn" className="bg-red-700 hover:bg-red-800" onClick={submit}>Conferma e genera nota</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
};
